import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { BookOpen } from "lucide-react";
import { CardStack } from "@/components/cardStack/CardStack";
import { EmptyState } from "@/components/ui/interactive-empty-state";
import { SpinnerIcon } from "@/components/ui/SpinnerIcon";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import type { StackableItem } from "@/lib/types";
import type { SwipeDirection } from "@/hooks/useCardRotation";
import { useI18n } from "@/lib/i18n";
import { formatTime } from "@/lib/time/format";
import { vocabularyLevelChecker } from "@/lib/vocabularyLevel";
import { useRouter } from "next/navigation";
import { cn } from "@/lib/utils";

interface PhraseSectionProps {
  groupedSentences: any[];
  vocabularyTerms: string[];
  vocabulary?: any;
  hiddenTermKeys?: string[];
  practiceHiddenWords?: Record<string, boolean>;
  isWordCheckerReady?: boolean;
  hasDictionarySelected?: boolean;
  isVocabularyLoading: boolean;
  onTimestampClick?: (timestamp: number, endTimestamp?: number) => void;
  isActive?: boolean;
  dictionaryLabel?: string;
}

interface PhraseCardItem extends StackableItem {
  term: string;
  sentence: string;
  translation?: string;
  timestamp?: [number, number];
  matchStart: number;
  matchEnd: number;
  matchedText: string;
}

const BATCH_SIZE = 24;
const LOAD_MORE_THRESHOLD = 5;

const buildFlexibleRegex = (term: string): RegExp | null => {
  const trimmed = term?.trim();
  if (!trimmed) return null;

  const escapeRegex = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const words = trimmed
    .split(/\s+/)
    .map((word) => escapeRegex(word))
    .filter(Boolean);

  if (!words.length) return null;

  if (words.length === 1) {
    const pattern = words[0];
    return new RegExp(`\\b${pattern}\\b`, "gi");
  }

  const pattern = words.join("\\s+");
  return new RegExp(`\\b${pattern}\\b`, "gi");
};

const highlightMatch = (sentence: string, start: number, end: number) => {
  if (start < 0 || end <= start) {
    return sentence;
  }

  return (
    <span>
      {sentence.slice(0, start)}
      <span className="bg-primary/10 text-primary font-semibold px-1 rounded">
        {sentence.slice(start, end)}
      </span>
      {sentence.slice(end)}
    </span>
  );
};

export const PhraseSection: React.FC<PhraseSectionProps> = ({
  groupedSentences,
  vocabularyTerms: _vocabularyTerms,
  vocabulary: _vocabulary,
  hiddenTermKeys: _hiddenTermKeys,
  practiceHiddenWords: _practiceHiddenWords,
  isWordCheckerReady = false,
  hasDictionarySelected = false,
  isVocabularyLoading,
  onTimestampClick,
  isActive = false,
  dictionaryLabel,
}) => {
  const { t } = useI18n();
  const router = useRouter();
  const [dictionaryRefreshToken, setDictionaryRefreshToken] = useState(0);
  const [isPrefetchingDictionary, setIsPrefetchingDictionary] = useState(false);
  const [allItems, setAllItems] = useState<PhraseCardItem[]>([]);
  const [generatedItems, setGeneratedItems] = useState<PhraseCardItem[]>([]);
  const [visibleCount, setVisibleCount] = useState(BATCH_SIZE);
  const chunkEntries = useMemo(() => {
    const entries: Array<{
      sentence: string;
      translation?: string | Record<string, string>;
      timestamp?: [number, number];
      chunkKey: string;
    }> = [];

    if (!Array.isArray(groupedSentences)) {
      return entries;
    }

    groupedSentences.forEach((group, groupIndex) => {
      const chunkList = Array.isArray(group?.chunks) && group.chunks.length > 0
        ? group.chunks
        : [{
            text: group?.text,
            translation: group?.translation,
            timestamp: group?.timestamp,
          }];

      chunkList.forEach((chunk: any, chunkIndex: number) => {
        const sentence = chunk?.text;
        if (typeof sentence !== "string" || !sentence.trim()) {
          return;
        }

        let translation = chunk?.translation ?? group?.translation;
        if (typeof translation === 'object' && translation !== null) {
            translation = translation['zh'] || Object.values(translation)[0] || '';
        }

        entries.push({
          sentence,
          translation: typeof translation === 'string' ? translation : undefined,
          timestamp: chunk?.timestamp ?? group?.timestamp,
          chunkKey: `${groupIndex}-${chunkIndex}`,
        });
      });
    });

    return entries;
  }, [groupedSentences]);

  useEffect(() => {
    let cancelled = false;

    if (!isWordCheckerReady || !chunkEntries.length) {
      return;
    }

    const textToPreload = chunkEntries
      .map((chunk) => chunk.sentence)
      .filter((sentence): sentence is string => typeof sentence === "string" && sentence.trim().length > 0)
      .join(" ");

    if (!textToPreload) {
      setDictionaryRefreshToken((token) => token + 1);
      setIsPrefetchingDictionary(false);
      return;
    }

    setIsPrefetchingDictionary(true);

    const preload = async () => {
      try {
        await vocabularyLevelChecker.preloadTextVocabulary(textToPreload);
      } catch (error) {
        console.error("Failed to preload vocabulary for PhraseSection:", error);
      } finally {
        if (!cancelled) {
          setDictionaryRefreshToken((token) => token + 1);
          setIsPrefetchingDictionary(false);
        }
      }
    };

    preload();

    return () => {
      cancelled = true;
      setIsPrefetchingDictionary(false);
    };
  }, [chunkEntries, isWordCheckerReady]);

  const [isGeneratingItems, setIsGeneratingItems] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const generateItems = async () => {
      if (!chunkEntries.length || !isWordCheckerReady) {
        if (!cancelled) {
          setGeneratedItems([]);
          setIsGeneratingItems(false);
        }
        return;
      }

      if (!cancelled) {
        setIsGeneratingItems(true);
      }

      const items: PhraseCardItem[] = [];
      const seenMatches = new Set<string>();
      const seenTerms = new Set<string>();
      const wordRegex = /\b[a-zA-Z]+(?:'[a-zA-Z]+)?\b/g;

      for (const chunk of chunkEntries) {
        const sentence = chunk.sentence;
        if (!sentence) continue;

        let match: RegExpExecArray | null;
        wordRegex.lastIndex = 0;
        while ((match = wordRegex.exec(sentence)) !== null) {
          const matchedText = match[0];
          const start = match.index ?? sentence.indexOf(matchedText);
          if (start < 0) continue;

          const end = start + matchedText.length;
          const normalized = matchedText.toLowerCase();

          let hasTranslation = false;
          try {
            hasTranslation = vocabularyLevelChecker.hasTranslation(normalized);
            if (!hasTranslation) {
              hasTranslation = await vocabularyLevelChecker.hasTranslationAsync(
                normalized
              );
            }
          } catch {
            hasTranslation = false;
          }

          if (!hasTranslation) {
            continue;
          }

          if (seenTerms.has(normalized)) {
            continue;
          }

          const signature = `${chunk.chunkKey}-${normalized}-${start}`;
          if (seenMatches.has(signature)) {
            continue;
          }

          seenMatches.add(signature);
          seenTerms.add(normalized);

          items.push({
            id: signature,
            term: matchedText,
            sentence,
            translation: typeof chunk.translation === 'string' ? chunk.translation : undefined,
            timestamp: Array.isArray(chunk.timestamp)
              ? chunk.timestamp
              : undefined,
            matchStart: start,
            matchEnd: end,
            matchedText,
          });
        }
      }

      items.sort((a, b) => {
        const timeA = a.timestamp?.[0] ?? Number.POSITIVE_INFINITY;
        const timeB = b.timestamp?.[0] ?? Number.POSITIVE_INFINITY;
        if (timeA !== timeB) {
          return timeA - timeB;
        }
        if (a.term.toLowerCase() !== b.term.toLowerCase()) {
          return a.term.localeCompare(b.term);
        }
        return a.matchStart - b.matchStart;
      });

      if (!cancelled) {
        setGeneratedItems(items);
        setIsGeneratingItems(false);
      }
    };

    generateItems();

    return () => {
      cancelled = true;
    };
  }, [chunkEntries, dictionaryRefreshToken, isWordCheckerReady]);

  const generationSignature = useMemo(
    () => generatedItems.map((item) => item.id).join("|"),
    [generatedItems]
  );
  const previousSignatureRef = useRef<string | null>(null);

  useEffect(() => {
    if (previousSignatureRef.current === generationSignature) {
      return;
    }

    previousSignatureRef.current = generationSignature;
    setAllItems(generatedItems);
    setVisibleCount(Math.min(BATCH_SIZE, generatedItems.length || BATCH_SIZE));
  }, [generationSignature, generatedItems]);

  const totalMatches = allItems.length;
  const visibleItems = useMemo(
    () => allItems.slice(0, Math.min(visibleCount, totalMatches)),
    [allItems, visibleCount, totalMatches]
  );

  const hasMoreItems = visibleItems.length < totalMatches;

  const loadMoreItems = useCallback(() => {
    if (!hasMoreItems) {
      return;
    }

    setVisibleCount((previous) =>
      Math.min(previous + BATCH_SIZE, totalMatches)
    );
  }, [hasMoreItems, totalMatches]);

  const ttsModulePromiseRef = useRef<Promise<typeof import("@/components/tts")> | null>(null);
  const ongoingSpeakAbortRef = useRef<AbortController | null>(null);
  const activeItemIdRef = useRef<string | null>(null);
  const lastSpokenRef = useRef<{ id: string; timestamp: number } | null>(null);
  const pendingSpeakTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    ttsModulePromiseRef.current = import("@/components/tts");
    return () => {
      if (ongoingSpeakAbortRef.current) {
        ongoingSpeakAbortRef.current.abort();
        ongoingSpeakAbortRef.current = null;
      }
      if (typeof window !== "undefined" && pendingSpeakTimeoutRef.current !== null) {
        window.clearTimeout(pendingSpeakTimeoutRef.current);
        pendingSpeakTimeoutRef.current = null;
      }
    };
  }, []);

  const speakTerm = useCallback(async (text: string | undefined) => {
    const trimmed = text?.trim();
    if (!trimmed) {
      return;
    }

    try {
      const modulePromise = ttsModulePromiseRef.current ?? import("@/components/tts");
      const { doSpeak } = await modulePromise;

      if (ongoingSpeakAbortRef.current) {
        ongoingSpeakAbortRef.current.abort();
      }

      const controller = new AbortController();
      ongoingSpeakAbortRef.current = controller;

      try {
        await doSpeak({
          provider: "EdgeTTS",
          text: trimmed,
          lang: "en",
          rate: 10,
          volume: 100,
          signal: controller.signal,
          onFinish: () => {
            if (ongoingSpeakAbortRef.current === controller) {
              ongoingSpeakAbortRef.current = null;
            }
          },
        });
      } finally {
        if (ongoingSpeakAbortRef.current === controller) {
          ongoingSpeakAbortRef.current = null;
        }
      }
    } catch (error: any) {
      if (error?.name === "AbortError") {
        return;
      }
      console.error("Failed to speak phrase term:", trimmed, error);
    }
  }, []);

  const handleItemSwipe = useCallback(
    (item: PhraseCardItem, direction: SwipeDirection) => {
      let updatedLength = totalMatches;

      setAllItems((previous) => {
        const index = previous.findIndex((entry) => entry.id === item.id);
        if (index === -1) {
          updatedLength = previous.length;
          return previous;
        }

        const next = [...previous];
        const [removed] = next.splice(index, 1);
        if (!removed) {
          updatedLength = previous.length;
          return previous;
        }

        if (direction === "left") {
          updatedLength = next.length;
          return next;
        }

        next.unshift(removed);
        updatedLength = next.length;
        return next;
      });

      setVisibleCount((previous) => Math.min(previous, updatedLength));
    },
    [totalMatches]
  );

  const handleActiveChange = useCallback(
    (active: PhraseCardItem | null) => {
      if (!active || !hasMoreItems) {
        return;
      }

      const currentVisible = visibleItems;
      const index = currentVisible.findIndex((item) => item.id === active.id);
      if (index === -1) {
        return;
      }

      const remaining = currentVisible.length - index - 1;
      if (remaining <= LOAD_MORE_THRESHOLD) {
        loadMoreItems();
      }
    },
    [hasMoreItems, loadMoreItems, visibleItems]
  );

  useEffect(() => {
    if (!hasMoreItems) {
      setVisibleCount((previous) => Math.min(previous, totalMatches));
    }
  }, [hasMoreItems, totalMatches]);

  useEffect(() => {
    if (typeof window !== "undefined" && pendingSpeakTimeoutRef.current !== null) {
      window.clearTimeout(pendingSpeakTimeoutRef.current);
      pendingSpeakTimeoutRef.current = null;
    }

    // 只有在 isActive 为 true 时才自动朗读
    if (!isActive) {
      activeItemIdRef.current = null;
      lastSpokenRef.current = null;
      return;
    }

  const topCard =
      visibleItems.length > 0
        ? visibleItems[visibleItems.length - 1]
        : undefined;

    if (!topCard) {
      activeItemIdRef.current = null;
      lastSpokenRef.current = null;
      return;
    }

    const topId = String(topCard.id);
    const now = Date.now();
    const last = lastSpokenRef.current;

    if (
      activeItemIdRef.current === topId &&
      last &&
      last.id === topId &&
      now - last.timestamp <= 1000
    ) {
      return;
    }

    activeItemIdRef.current = topId;

    const speak = () => {
      lastSpokenRef.current = { id: topId, timestamp: Date.now() };
      pendingSpeakTimeoutRef.current = null;
      void speakTerm(topCard.term);
    };

    if (typeof window !== "undefined") {
      pendingSpeakTimeoutRef.current = window.setTimeout(speak, 250);
    } else {
      speak();
    }
  }, [speakTerm, visibleItems, isActive]);

  const countLabelText = totalMatches === 1
    ? t("phraseSection.countLabel.singular", "dictionary word")
    : t("phraseSection.countLabel.plural", "dictionary words");
  const displayCount = `${totalMatches}`;
  const isTruncated = visibleItems.length < totalMatches;
  const truncateTemplate = t(
    "phraseSection.truncateNotice",
    "Showing the first {count} matches"
  );
  const truncateMessage = truncateTemplate.replace(
    "{count}",
    String(Math.min(visibleCount, totalMatches))
  );
  const cardsIndicatorLabel = t(
    "phraseSection.indicators.cardsLabel",
    "Vocabulary"
  );
  const dictionaryIndicatorLabel = t(
    "phraseSection.indicators.dictionaryLabel",
    "Current dictionary"
  );
  const dictionaryIndicatorValue =
    dictionaryLabel?.trim() ||
    t(
      "phraseSection.indicators.dictionaryMissing",
      "Dictionary not selected"
    );

  const renderCardStackIndicators = (options?: {
    className?: string;
    style?: React.CSSProperties;
    fullBleed?: boolean;
  }) => {
    const bleedVar = "var(--tdt-vocab-bleed, 24px)";
    const fullBleedStyles = options?.fullBleed
      ? ({
          marginLeft: `calc(-1 * ${bleedVar})`,
          marginRight: `calc(-1 * ${bleedVar})`,
          paddingLeft: `calc(${bleedVar})`,
          paddingRight: `calc(${bleedVar})`,
          width: `calc(100% + 2 * ${bleedVar})`,
        } satisfies React.CSSProperties)
      : undefined;

    return (
      <div
        className={cn(
          "grid w-full grid-cols-[1fr_auto] items-start gap-3 rounded-2xl border border-border/40 bg-muted/40 px-3 py-2",
          options?.fullBleed && "max-w-none self-stretch",
          options?.className
        )}
        style={{ ...fullBleedStyles, ...options?.style }}
      >
        <div className="flex flex-col gap-0.5">
          <p className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground/70">
            {cardsIndicatorLabel}
          </p>
          <div className="flex items-baseline gap-1">
            <span className="text-xl font-semibold text-foreground">
              {displayCount}
            </span>
            <span className="text-[11px] text-muted-foreground/80">
              {countLabelText}
            </span>
          </div>
        </div>
        <div className="text-center sm:text-right">
          <p className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground/70 pb-1.5">
            {dictionaryIndicatorLabel}
          </p>
          <p className="text-[13px] font-semibold text-foreground">
            {dictionaryIndicatorValue}
          </p>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (isVocabularyLoading) {
      return (
        <div className="flex h-80 items-center justify-center">
          <SpinnerIcon size={40} color="hsl(var(--primary))" />
        </div>
      );
    }

    if (!isWordCheckerReady) {
      if (!hasDictionarySelected) {
        const settingsActionLabel = t(
          "phraseSection.actions.openSettings",
          "Open settings"
        );
        return (
          <EmptyState
            title={t(
              "phraseSection.empty.noDictionary.title",
              "Choose a dictionary to unlock highlights"
            )}
            description={t(
              "phraseSection.empty.noDictionary.description",
              "Select a vocabulary dictionary in settings so we can spot words you’ve learned."
            )}
            illustration="/images/empty-vocabulary.svg"
            className="my-12"
            action={{
              label: settingsActionLabel,
              onClick: () => router.push("/settings"),
            }}
          />
        );
      }

      return (
        <div className="flex h-80 items-center justify-center">
          <SpinnerIcon size={40} color="hsl(var(--primary))" />
        </div>
      );
    }

    if ((isPrefetchingDictionary || isGeneratingItems) && !visibleItems.length) {
      return (
        <div className="flex h-80 items-center justify-center">
          <SpinnerIcon size={40} color="hsl(var(--primary))" />
        </div>
      );
    }

    if (!visibleItems.length) {
      return (
        <EmptyState
          title={t("phraseSection.empty.noMatches.title", "No contextual phrases yet")}
          description={t(
            "phraseSection.empty.noMatches.description",
            "Vocabulary terms haven’t appeared in the transcript so far."
          )}
          illustration="/images/empty-transcript.svg"
          className="my-12"
        />
      );
    }

    const renderPhraseCard = (item: PhraseCardItem) => (
      <Card className="flex h-full w-full flex-col rounded-[32px] border bg-card shadow-sm transition hover:shadow-lg">
        <CardContent className="flex h-full flex-col justify-between sm:space-y-4 space-y-2 p-3 sm:p-8">
          <div className="sm:space-y-4 space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs capitalize tracking-wide">
              <span className="inline-flex sm:text-xl text-[14px] items-center gap-2 rounded-full border border-border/60 px-3 py-1 font-semibold">
                {item.term}
              </span>
              {item.timestamp && (
                <span className="font-mono text-muted-foreground/70">
                  {formatTime(item.timestamp[0])}
                </span>
              )}
            </div>

            <div className="space-y-3 sm:text-base text-[13px] leading-relaxed text-foreground">
              {highlightMatch(
                item.sentence,
                item.matchStart,
                item.matchEnd
              )}
            </div>

            {item.translation && (
              <p className="sm:rounded-2xl rounded-xl border border-border/40 bg-muted/30 sm:px-4 px-2 sm:py-3 py-2 sm:text-sm text-xs leading-relaxed text-muted-foreground">
                {item.translation}
              </p>
            )}
          </div>

          <div className="flex items-center justify-between sm:text-sm text-xs font-medium text-muted-foreground/80">
            <div className="flex items-center gap-2 text-[8px] uppercase tracking-wide">
              <span>{t("phraseSection.badges.dictionaryMode", "Dictionary mode")}</span>
            </div>

            {onTimestampClick && item.timestamp && (
              <Button
                size="sm"
                variant="outline"
                className="rounded-xl border-border/60"
                onClick={() =>
                  onTimestampClick(item.timestamp![0], item.timestamp![1])
                }
              >
                {t("phraseSection.jump", "Jump to clip")}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );

    return (
      <div className="flex flex-col items-center gap-6 w-full">
        <div className="sm:hidden w-full space-y-2.5">
          {renderCardStackIndicators({
            fullBleed: true,
          })}
          <CardStack<PhraseCardItem>
            items={visibleItems}
            containerClassName="relative mx-auto sm:h-[24rem] h-[16rem] w-full max-w-[360px]"
            cardClassName="absolute inset-0 h-full w-full cursor-grab"
            stackConfig={{ rotation: 2, scale: 0.04, perspective: 900 }}
            onActiveChange={handleActiveChange}
            onItemSwipe={handleItemSwipe}
          >
            {(item) => renderPhraseCard(item)}
          </CardStack>
        </div>

        <div className="hidden w-full sm:flex sm:flex-col sm:gap-4">
          {renderCardStackIndicators()}
          <div className="hidden w-full gap-4 sm:grid sm:grid-cols-[repeat(auto-fill,minmax(320px,1fr))]">
            {visibleItems.map((item) => (
              <div key={item.id}>{renderPhraseCard(item)}</div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <section className="relative w-full">
      <div className="flex flex-col w-full rounded-3xl sm:rounded border-none border-border/50 sm:bg-background/30 bg-transparent sm:backdrop-blur-sm backdrop-blur-none overflow-hidden">
        <div className="z-20 hidden flex-col gap-3 p-4 sm:flex sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="h-6 w-2 rounded-full bg-primary" />
            <div>
              <h3 className="text-xl font-bold text-foreground">
                {t("phraseSection.title", "Vocabulary in Context")}
              </h3>
              <p className="text-sm text-muted-foreground">
                {t(
                  "phraseSection.subtitle",
                  "Review every vocabulary term inside the transcript with its sentence and translation."
                )}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
              <BookOpen className="h-4 w-4" />
              <span className="whitespace-nowrap">{displayCount} {countLabelText}</span>
            </span>
            {isTruncated && (
              <span className="text-xs text-muted-foreground">
                {truncateMessage}
              </span>
            )}
          </div>
        </div>

        <div className="flex-1 px-8 pb-4 sm:px-4">
          {renderContent()}
        </div>
      </div>
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-white via-white/80 to-transparent dark:from-background dark:via-background/70 hidden sm:block" />
    </section>
  );
};
